/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Video Debug Overlay ViewModel — visibility, mode and the
 * once-per-second metrics snapshot.
 *
 * ## The activation paths converge here
 *
 * A remote key sequence, a desktop hotkey, the "Stream Stats" item in the
 * playback action bar and the persisted developer toggle all end up
 * dispatching into this slice, so `isVisible` is the single source of
 * truth the proposal §4 asks for and the four can never disagree.
 *
 * Each transient trigger owns a mode: the playback menu shows compact,
 * the key combination shows expanded, and either closes the panel when
 * its own mode is the one already showing.
 *
 * `isVisible` and `isPersistedEnabled` are deliberately separate. The
 * transient paths (key sequence, hotkey, playback menu) show and hide for
 * this session only; only the developer toggle writes through to storage.
 * Collapsing them would mean a QA engineer who pressed the hotkey once
 * silently acquired an always-on overlay on their device.
 *
 * Every metric is shown to every session. The overlay ships in all builds
 * and nothing here is gated on the build type: reaching it at all takes a
 * deliberate gesture that normal use never produces.
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  readVideoDebugOverlayEnabled,
  writeVideoDebugOverlayEnabled
} from "../../devtools/videoDebugOverlayStorage";
import { Log } from "../../model/logging";
import {
  DebugOverlayMode,
  WebVideoMetricsProvider,
  buildDebugMetricGroups,
  copyDebugMetricsToClipboard,
  formatDebugMetricsForExport
} from "../../model/videoDebugOverlay";
import type {
  IVideoMetricsProvider,
  TDebugMetricGroup,
  TDebugMetrics
} from "../../model/videoDebugOverlay";
import { getThumbnailUrlTemplate } from "../videoPlayerUi";
import { INITIAL_STATE } from "./videoDebugOverlay.types";
import type {
  VideoDebugMetricsPayload,
  VideoDebugOverlayState,
  VideoDebugVisibilityPayload
} from "./videoDebugOverlay.types";

const TAG: string = "videoDebugOverlaySlice";

/** Every thunk and selector below only reads `state.videoDebugOverlay`. */
type TVideoDebugOverlayRootState = {
  videoDebugOverlay: VideoDebugOverlayState;
};

/**
 * The metrics provider, held outside Redux because it is a live adapter
 * over the player and carries the bitrate dwell timer — neither of which
 * is serialisable state. Module-level rather than per-dispatch so that
 * timer survives across poll ticks.
 */
const metricsProvider: IVideoMetricsProvider = new WebVideoMetricsProvider();

/**
 * Merge in the filmstrip template, which the metrics provider cannot see.
 *
 * Thumbnails are an application concern, not a player one: the template is
 * resolved from the channel's tuning URIs by `videoPlayerUi`, never by
 * Shaka. Adding it here rather than giving the provider a second data
 * source keeps `IVideoMetricsProvider` a pure player adapter that the
 * Android and iOS implementations can satisfy unchanged.
 *
 */
function withThumbnailUrl(metrics: TDebugMetrics): TDebugMetrics {
  const template: string = getThumbnailUrlTemplate();
  return {
    ...metrics,
    source: {
      ...metrics.source,
      thumbnailUrl: template.length > 0 ? template : null
    }
  };
}

/**
 * Where a trigger asking for `requestedMode` should leave the overlay.
 *
 * Each entry point owns a mode — the playback menu opens compact, the key
 * combination opens expanded — and the rule is the same for both: a
 * trigger whose mode is already on screen closes the panel, and a trigger
 * whose mode is not on screen switches to it.
 *
 * That makes each trigger a plain toggle of its own view while still
 * letting a tester go from compact to expanded in one press, instead of
 * closing and reopening.
 */
function nextVisibility(
  isVisible: boolean,
  mode: DebugOverlayMode,
  requestedMode: DebugOverlayMode
): VideoDebugVisibilityPayload {
  if (isVisible && mode === requestedMode) {
    return { isVisible: false, mode: DebugOverlayMode.COMPACT };
  }
  return { isVisible: true, mode: requestedMode };
}

/**
 * Restore the persisted preference.
 *
 * Dispatched once from the app shell. Safe to dispatch again — it only
 * ever re-reads, never writes.
 */
export const initialiseVideoDebugOverlay = createAsyncThunk<
  void,
  void,
  { state: TVideoDebugOverlayRootState }
>("videoDebugOverlay/initialise", async (_: void, { dispatch }) => {
  try {
    dispatch(persistedPreferenceRestored(readVideoDebugOverlayEnabled()));
  } catch (error: unknown) {
    Log.e(TAG, "initialiseVideoDebugOverlay()", error);
  }
});

/**
 * Take one snapshot and project it onto the rows the panel renders.
 *
 * Called on the 1 s poll while the overlay is visible. Everything it
 * touches is guarded — a metrics read that fails must never interrupt
 * playback, and a panel showing stale numbers beats a panel that crashed.
 */
export const refreshVideoDebugMetrics = createAsyncThunk<
  void,
  void,
  { state: TVideoDebugOverlayRootState }
>(
  "videoDebugOverlay/refreshMetrics",
  async (_: void, { dispatch, getState }) => {
    try {
      const { isVisible, mode } = getState().videoDebugOverlay;
      if (!isVisible) {
        return;
      }

      const raw: TDebugMetrics = withThumbnailUrl(
        metricsProvider.getSnapshot()
      );
      const groups: ReadonlyArray<TDebugMetricGroup> = buildDebugMetricGroups(
        raw,
        mode
      );

      dispatch(metricsUpdated({ groups, timestampMs: Date.now() }));
    } catch (error: unknown) {
      Log.e(TAG, "refreshVideoDebugMetrics()", error);
    }
  }
);

/**
 * Show the overlay in `requestedMode`, or close it when that mode is
 * already on screen.
 *
 * The transient path, used by all three non-persisting triggers:
 *
 * | Trigger | Mode it owns |
 * | --- | --- |
 * | "Stream Stats" in the playback action bar | Compact |
 * | UP UP DOWN DOWN RIGHT on a remote | Expanded |
 * | Ctrl+Shift+D on desktop | Expanded |
 *
 * Does not touch the persisted preference — a tester who pressed the
 * hotkey once must not silently acquire an always-on overlay.
 *
 * @param requestedMode - The mode this trigger owns.
 */
export const toggleVideoDebugOverlay = createAsyncThunk<
  void,
  DebugOverlayMode,
  { state: TVideoDebugOverlayRootState }
>(
  "videoDebugOverlay/toggle",
  async (requestedMode: DebugOverlayMode, { dispatch, getState }) => {
    const { isVisible, mode } = getState().videoDebugOverlay;
    const next: VideoDebugVisibilityPayload = nextVisibility(
      isVisible,
      mode,
      requestedMode
    );
    dispatch(visibilityUpdated(next));

    if (next.isVisible) {
      // Refresh eagerly so the panel is populated on the frame it appears
      // rather than blank until the first poll tick a second later. Also
      // covers a mode switch, where the rows themselves change.
      dispatch(refreshVideoDebugMetrics());
    } else {
      metricsProvider.reset();
    }
  }
);

/**
 * Set and persist the developer preference, applying it immediately.
 *
 * The persisting path: used by the developer toggle. Turning it on shows
 * the overlay in compact mode; turning it off hides it.
 */
export const setVideoDebugOverlayEnabled = createAsyncThunk<
  void,
  boolean,
  { state: TVideoDebugOverlayRootState }
>("videoDebugOverlay/setEnabled", async (isEnabled: boolean, { dispatch }) => {
  try {
    writeVideoDebugOverlayEnabled(isEnabled);
  } catch (error: unknown) {
    Log.e(TAG, "setVideoDebugOverlayEnabled()", error);
  }

  dispatch(persistedPreferenceRestored(isEnabled));

  if (isEnabled) {
    dispatch(refreshVideoDebugMetrics());
  } else {
    metricsProvider.reset();
  }
});

/**
 * Copy the rows currently on screen to the clipboard for a bug report,
 * falling back to the log when the clipboard is unavailable — which it
 * routinely is on Smart TV browsers (UAW-3129).
 */
export const copyVideoDebugMetrics = createAsyncThunk<
  boolean,
  void,
  { state: TVideoDebugOverlayRootState }
>("videoDebugOverlay/copyMetrics", async (_: void, { getState }) => {
  const { groups } = getState().videoDebugOverlay;
  const text: string = formatDebugMetricsForExport(groups);
  return copyDebugMetricsToClipboard(text);
});

/**
 * Drop the snapshot and the provider's carried state. Dispatched when the
 * player screen unmounts so a later tune does not inherit the previous
 * stream's ABR dwell timer or flash its numbers.
 */
export const resetVideoDebugMetrics = createAsyncThunk<
  void,
  void,
  { state: TVideoDebugOverlayRootState }
>("videoDebugOverlay/resetMetrics", async (_: void, { dispatch }) => {
  metricsProvider.reset();
  dispatch(metricsCleared());
});

const videoDebugOverlaySlice = createSlice({
  name: "videoDebugOverlay",
  initialState: INITIAL_STATE,
  reducers: {
    /** Applies a visibility + mode transition. */
    visibilityUpdated(
      state: VideoDebugOverlayState,
      action: PayloadAction<VideoDebugVisibilityPayload>
    ): void {
      state.isVisible = action.payload.isVisible;
      state.mode = action.payload.mode;
      if (!action.payload.isVisible) {
        state.groups = [];
        state.lastUpdatedAtMs = 0;
      }
    },

    /** Replaces the rendered rows with a newer projection. */
    metricsUpdated(
      state: VideoDebugOverlayState,
      action: PayloadAction<VideoDebugMetricsPayload>
    ): void {
      state.groups = action.payload.groups;
      state.lastUpdatedAtMs = action.payload.timestampMs;
    },

    /** Clears the rendered rows without changing visibility. */
    metricsCleared(state: VideoDebugOverlayState): void {
      state.groups = [];
      state.lastUpdatedAtMs = 0;
    },

    /**
     * Applies the persisted preference and mirrors it onto visibility.
     * Restoring `true` shows the overlay; restoring `false` hides it,
     * which is also what makes the developer toggle an off switch.
     */
    persistedPreferenceRestored(
      state: VideoDebugOverlayState,
      action: PayloadAction<boolean>
    ): void {
      state.isPersistedEnabled = action.payload;
      state.isVisible = action.payload;
      if (action.payload) {
        state.mode = DebugOverlayMode.COMPACT;
      } else {
        state.groups = [];
        state.lastUpdatedAtMs = 0;
      }
    }
  }
});

export const {
  metricsCleared,
  metricsUpdated,
  persistedPreferenceRestored,
  visibilityUpdated
} = videoDebugOverlaySlice.actions;

export default videoDebugOverlaySlice.reducer;

/* ── Selectors ────────────────────────────────────────────────────────── */

/** Whether the diagnostics panel is currently on screen. */
export const selectIsVideoDebugOverlayVisible = (
  state: TVideoDebugOverlayRootState
): boolean => state.videoDebugOverlay.isVisible;

/** The rows to render, already filtered and formatted. */
export const selectVideoDebugOverlayGroups = (
  state: TVideoDebugOverlayRootState
): ReadonlyArray<TDebugMetricGroup> => state.videoDebugOverlay.groups;

/** Compact or expanded. */
export const selectVideoDebugOverlayMode = (
  state: TVideoDebugOverlayRootState
): DebugOverlayMode => state.videoDebugOverlay.mode;

/** The persisted developer preference, independent of current visibility. */
export const selectIsVideoDebugOverlayPersisted = (
  state: TVideoDebugOverlayRootState
): boolean => state.videoDebugOverlay.isPersistedEnabled;
